import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { GraduationCap, X } from "lucide-react";
import dayjs from "dayjs";
import TimePickerCst from "@components/InputCst/TimePickerCst";
import { EditionSession } from "../types";

interface RescheduleSessionModalProps {
  open: boolean;
  session: EditionSession;
  sessionNumber: number;
  contextLabel?: string;
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: (startDateIso: string, endDateIso: string) => void;
}

const RescheduleSessionModal: React.FC<RescheduleSessionModalProps> = ({
  open,
  session,
  sessionNumber,
  contextLabel,
  isSubmitting,
  onClose,
  onConfirm,
}) => {
  const theme = useTheme();
  const { t } = useTranslation(["session-detail", "session-wizard"]);

  const [start, setStart] = useState<string | null>(
    session.start_date ? dayjs(session.start_date).toISOString() : null,
  );
  const [end, setEnd] = useState<string | null>(
    session.end_date ? dayjs(session.end_date).toISOString() : null,
  );
  const [attempted, setAttempted] = useState(false);

  const errors = useMemo(() => {
    const e: { start?: string; end?: string } = {};
    if (!start) e.start = String(t("session-wizard:errors.required"));
    if (!end) e.end = String(t("session-wizard:errors.required"));
    if (start && end && !dayjs(end).isAfter(dayjs(start))) {
      e.end = String(t("session-wizard:errors.end-before-start"));
    }
    return e;
  }, [start, end, t]);

  const valid = !errors.start && !errors.end;

  const handleConfirm = () => {
    setAttempted(true);
    if (!valid || !start || !end) return;
    onConfirm(start, end);
  };

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          padding: "20px 24px 8px",
          color: theme.palette.text.primary,
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            minWidth: 0,
          }}
        >
          {contextLabel && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                color: theme.palette.text.primary,
                fontSize: "12px",
                fontWeight: 600,
              }}
            >
              <GraduationCap size={14} />
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {contextLabel}
              </Typography>
            </Box>
          )}
          <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>
            {t("session-detail:reschedule-modal.title", {
              number: sessionNumber,
            })}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          aria-label="close"
          disabled={isSubmitting}
          sx={{ color: theme.palette.text.primary }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: "8px 24px 24px",
          "& .MuiOutlinedInput-root": { borderRadius: "8px" },
          "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
        }}
      >
        <Grid container spacing={2} sx={{ mt: "10px" }}>
          <Grid item xs={12} md={6}>
            <TimePickerCst
              id="reschedule-session-start"
              label={String(t("session-wizard:main.start-date"))}
              value={start ? dayjs(start) : null}
              onChange={(v: any) => setStart(v ? dayjs(v).toISOString() : null)}
              required
              error={attempted && !!errors.start}
              errorMessage={attempted ? errors.start : undefined}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TimePickerCst
              id="reschedule-session-end"
              label={String(t("session-wizard:main.end-date"))}
              value={end ? dayjs(end) : null}
              onChange={(v: any) => setEnd(v ? dayjs(v).toISOString() : null)}
              required
              minDate={start || undefined}
              error={attempted && !!errors.end}
              errorMessage={attempted ? errors.end : undefined}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ padding: "12px 24px 20px" }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isSubmitting}
          sx={{ textTransform: "none", borderRadius: "8px" }}
        >
          {t("session-detail:reschedule-modal.cancel")}
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isSubmitting || (attempted && !valid)}
          sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 700 }}
        >
          {isSubmitting
            ? t("session-detail:reschedule-modal.submitting")
            : t("session-detail:reschedule-modal.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RescheduleSessionModal;
